import { useEffect, useMemo, useState } from "react";
import { listCategoryMap, saveCategoryMap, type CategoryEdit, type CategoryRow } from "./actions";

// The category mapping, one row per (feed, feed category): what the feed provides, the WooCommerce store category it belongs in, and the Google product category Merchant
// Center receives. A row passes the approvals ONLY once it is confirmed here; the proposals are suggestions (the store one from how the old store filed the same products,
// the Google one from a scored text match), never facts.

const PAGE = 100;
const STATUS_LABEL: Record<string, string> = { proposed: "Proposed", unmapped: "Unmapped", confirmed: "Confirmed" };

export function CategoriesPanel() {
  const [rows, setRows] = useState<CategoryRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [feed, setFeed] = useState("");
  const [status, setStatus] = useState("open");
  const [stockedOnly, setStockedOnly] = useState(true);
  const [text, setText] = useState("");
  const [page, setPage] = useState(0);
  const [edits, setEdits] = useState<Record<string, { store?: string; google?: string }>>({});
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [taxonomy, setTaxonomy] = useState<{ id: number; path: string }[]>([]);

  const load = () => listCategoryMap().then((r) => (r.ok ? setRows(r.data.rows) : setErr(r.error)));
  useEffect(() => { load(); }, []);
  useEffect(() => {          // Google's taxonomy file (a static file next to the console), only for the type-ahead on the Google column
    fetch("/google_product_taxonomy.txt").then((r) => r.text()).then((t) => setTaxonomy(
      t.split("\n").map((l) => /^(\d+) - (.+)$/.exec(l.trim())).filter((m): m is RegExpExecArray => !!m).map((m) => ({ id: Number(m[1]), path: m[2] })))).catch(() => undefined);
  }, []);
  useEffect(() => { setPage(0); }, [feed, status, stockedOnly, text]);

  const feeds = useMemo(() => [...new Set((rows ?? []).map((r) => r.source_id))].sort(), [rows]);
  const storeOptions = useMemo(() => [...new Set((rows ?? []).map((r) => r.store_category).filter(Boolean))].sort(), [rows]);
  const idOf = useMemo(() => new Map(taxonomy.map((t) => [t.path, t.id])), [taxonomy]);
  const value = (r: CategoryRow) => ({ store: edits[r.id]?.store ?? r.store_category, google: edits[r.id]?.google ?? r.google_category });

  const shown = useMemo(() => {
    const q = text.trim().toLowerCase();
    return (rows ?? []).filter((r) => (!feed || r.source_id === feed) && (status === "all" || (status === "open" ? r.status !== "confirmed" : r.status === status))
      && (!stockedOnly || r.in_stock > 0) && (!q || r.feed_category.toLowerCase().includes(q) || r.store_category.toLowerCase().includes(q) || r.google_category.toLowerCase().includes(q)))
      .sort((a, b) => b.in_stock - a.in_stock || b.products - a.products);
  }, [rows, feed, status, stockedOnly, text]);
  const pageRows = shown.slice(page * PAGE, (page + 1) * PAGE);
  const stats = useMemo(() => {
    const all = rows ?? [];
    const conf = all.filter((r) => r.status === "confirmed");
    return { rows: all.length, confirmed: conf.length, stockedLeft: all.filter((r) => r.status !== "confirmed").reduce((n, r) => n + r.in_stock, 0), stocked: all.reduce((n, r) => n + r.in_stock, 0) };
  }, [rows]);

  const toEdit = (r: CategoryRow, status?: CategoryEdit["status"]): CategoryEdit => {
    const v = value(r);
    const e: CategoryEdit = { id: r.id };
    if (v.store !== r.store_category) e.store_category = v.store;
    if (v.google !== r.google_category) { e.google_category = v.google; e.google_category_id = idOf.get(v.google) ?? null; }
    if (status) e.status = status;
    return e;
  };
  const save = async (rowsToSave: CategoryRow[], status?: CategoryEdit["status"]) => {
    if (!rowsToSave.length) return;
    setBusy(true); setMsg(null);
    let done = 0, queued = 0, full = false;
    for (let i = 0; i < rowsToSave.length; i += 400) {
      const r = await saveCategoryMap(rowsToSave.slice(i, i + 400).map((x) => toEdit(x, status)));
      if (!r.ok) { setErr(r.error); setBusy(false); return; }
      done += r.data.saved;
      if (typeof r.data.queued === "number") queued += r.data.queued; else full = true;
    }
    setBusy(false); setPicked(new Set()); setEdits({});
    setMsg(`Saved ${done} row${done === 1 ? "" : "s"}; ${full ? "a full approval rebuild is queued" : `${queued.toLocaleString()} product${queued === 1 ? "" : "s"} queued for an approval rebuild`} (the watcher starts it within minutes).`);
    load();
  };
  const withBoth = (r: CategoryRow) => !!(value(r).store || value(r).google);

  if (err) return <div className="panel error">{err}</div>;
  if (!rows) return <div className="meta" style={{ padding: 16 }}>Loading the category mapping…</div>;
  return (
    <div className="categories">
      <div className="meta" style={{ margin: "0 0 8px" }}>
        {stats.confirmed.toLocaleString()} of {stats.rows.toLocaleString()} rows confirmed · {stats.stockedLeft.toLocaleString()} of {stats.stocked.toLocaleString()} in-stock products still wait on an unconfirmed row.
        A product passes the two category checks only when its row is confirmed.
      </div>
      <div className="sheet-controls">
        <label className="sheet-filter"><span>Feed</span><select value={feed} onChange={(e) => setFeed(e.target.value)}><option value="">All feeds</option>{feeds.map((f) => <option key={f} value={f}>{f}</option>)}</select></label>
        <label className="sheet-filter"><span>Status</span><select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="open">Not confirmed yet</option><option value="proposed">Proposed</option><option value="unmapped">Unmapped</option><option value="confirmed">Confirmed</option><option value="all">All</option></select></label>
        <label className="sheet-filter"><input type="checkbox" checked={stockedOnly} onChange={(e) => setStockedOnly(e.target.checked)} /><span>Only categories with in-stock products</span></label>
        <input type="search" className="sheet-search" placeholder="Search a category…" value={text} onChange={(e) => setText(e.target.value)} />
        <button type="button" disabled={busy || !picked.size} onClick={() => save(shown.filter((r) => picked.has(r.id) && withBoth(r)), "confirmed")}>Confirm selected ({picked.size})</button>
        <button type="button" disabled={busy || !pageRows.some(withBoth)} onClick={() => save(pageRows.filter(withBoth), "confirmed")} title="Confirms the rows on this page exactly as shown, including any edit you made">Confirm all {pageRows.filter(withBoth).length} on this page</button>
      </div>
      {msg && <div className="panel" style={{ margin: "8px 0" }}>{msg}</div>}
      <datalist id="store-cats">{storeOptions.map((o) => <option key={o} value={o} />)}</datalist>
      <datalist id="google-cats">{taxonomy.map((t) => <option key={t.id} value={t.path} />)}</datalist>
      <div className="sheet-scroll">
        <table className="manual-products sheet-table">
          <thead><tr><th><input type="checkbox" aria-label="Select the rows on this page" checked={pageRows.length > 0 && pageRows.every((r) => picked.has(r.id))}
              onChange={(e) => setPicked(e.target.checked ? new Set([...picked, ...pageRows.map((r) => r.id)]) : new Set([...picked].filter((id) => !pageRows.some((r) => r.id === id))))} /></th>
            <th>Feed</th><th title="What the vendor's own feed calls it">Feed category</th><th>Products</th><th>In stock</th>
            <th title="The WooCommerce product category">Store category</th><th>Evidence</th><th title="What Merchant Center receives">Google category</th><th>Match</th><th>Status</th></tr></thead>
          <tbody>
            {pageRows.map((r) => {
              const v = value(r);
              const dirty = v.store !== r.store_category || v.google !== r.google_category;
              return (
                <tr key={r.id} className={dirty ? "selected" : ""}>
                  <td><input type="checkbox" aria-label="Select this row" checked={picked.has(r.id)} onChange={(e) => { const n = new Set(picked); if (e.target.checked) n.add(r.id); else n.delete(r.id); setPicked(n); }} /></td>
                  <td>{r.source_id}</td><td>{r.feed_category}</td><td className="num">{r.products.toLocaleString()}</td><td className="num">{r.in_stock.toLocaleString()}</td>
                  <td><input list="store-cats" value={v.store} style={{ width: 240 }} onChange={(e) => setEdits({ ...edits, [r.id]: { ...edits[r.id], store: e.target.value } })} placeholder="Store category" /></td>
                  <td className="meta" style={{ margin: 0 }}>{r.store_basis}</td>
                  <td><input list="google-cats" value={v.google} style={{ width: 300 }} onChange={(e) => setEdits({ ...edits, [r.id]: { ...edits[r.id], google: e.target.value } })} placeholder="Google product category"
                       title={v.google && !idOf.has(v.google) && taxonomy.length ? "Not a category in Google's taxonomy" : ""} aria-invalid={!!(v.google && taxonomy.length && !idOf.has(v.google))} /></td>
                  <td className="num">{r.google_confidence ? `${Math.round(r.google_confidence * 100)}%` : ""}</td>
                  <td><span className={`tile${r.status === "confirmed" ? "" : " warn"}`} style={{ padding: "1px 6px" }}>{STATUS_LABEL[r.status]}</span>{dirty ? " edited" : ""}</td>
                </tr>);
            })}
          </tbody>
        </table>
        {shown.length === 0 && <p className="meta" style={{ padding: 16 }}>No category row matches.</p>}
      </div>
      <div className="sheet-pager" role="navigation" aria-label="Pagination">
        <span className="meta">{shown.length ? `Rows ${(page * PAGE + 1).toLocaleString()}–${(page * PAGE + pageRows.length).toLocaleString()} of ${shown.length.toLocaleString()}` : "No rows"}</span>
        <span className="sheet-pager-buttons">
          <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
          <button type="button" disabled={(page + 1) * PAGE >= shown.length} onClick={() => setPage(page + 1)}>Next</button>
        </span>
      </div>
    </div>
  );
}
